import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { clampFlexible, deriveDateFrom, getAnchor, getMaxDate, getMinDate, parseDate, toISO } from '../lib/dateRange';
import { formatDateValue } from '../lib/formatDateLabel';
import type { DateRange } from '@/shared/model';

export interface UseDateFrom {
  controlActivated: Ref<boolean>;

  selection: ComputedRef<DateRange>;
  anchorIso: ComputedRef<string>;
  flexible: ComputedRef<number>;
  flexibleForCalendar: ComputedRef<number | null>;
  valueLabel: ComputedRef<string>;
  minDate: ComputedRef<string>;
  maxDate: ComputedRef<string>;

  open: () => void;
  close: () => void;
  toggle: () => void;
  setAnchorFromISO: (payload: string | { start?: string } | null | undefined) => void;
  setFlexible: (n: number) => void;
  validate: () => boolean;
}

export interface UseDateFromOptions {
  model: Ref<DateRange>;
}

export function useDateFrom(options: UseDateFromOptions): UseDateFrom {
  const { model } = options;
  const controlActivated = ref(false);

  const selection = computed<DateRange>(() => model.value);

  const anchorIso = computed<string>(() => toISO(getAnchor(model.value)));

  const flexible = computed<number>(() => model.value.daysFlexible);

  const flexibleForCalendar = computed<number | null>(() => model.value.daysFlexible || null);

  const valueLabel = computed<string>(() => formatDateValue(model.value, getAnchor(model.value)));

  const minDate = computed<string>(() => toISO(getMinDate()));
  const maxDate = computed<string>(() => toISO(getMaxDate()));

  const open = (): void => {
    controlActivated.value = true;
  };

  const close = (): void => {
    controlActivated.value = false;
  };

  const toggle = (): void => {
    controlActivated.value = !controlActivated.value;
  };

  const setAnchorFromISO = (payload: string | { start?: string } | null | undefined): void => {
    const iso = typeof payload === 'string' ? payload : payload?.start;
    if (!iso) {
      return;
    }
    model.value = deriveDateFrom(parseDate(iso), model.value.daysFlexible);
  };

  const setFlexible = (n: number): void => {
    const next = n === model.value.daysFlexible ? 0 : clampFlexible(n);
    model.value = deriveDateFrom(getAnchor(model.value), next);
  };

  const validate = (): boolean => !!model.value.from;

  return {
    controlActivated,
    selection,
    anchorIso,
    flexible,
    flexibleForCalendar,
    valueLabel,
    minDate,
    maxDate,
    open,
    close,
    toggle,
    setAnchorFromISO,
    setFlexible,
    validate,
  };
}
